"use client";

import { useEffect, useRef } from "react";

/**
 * Cursor-reactive dot lattice for the hero background. Draws a low-opacity grid
 * of dots matching the site's static hero texture; dots within a radius of the
 * pointer brighten toward --accent and scale up, falling off with distance.
 *
 * Progressive enhancement: paints a single static baseline frame on mount, so
 * reduced-motion and touch devices get the plain lattice with no rAF loop and
 * no pointer listeners. No-JS visitors never see this (a .js-hidden CSS lattice
 * in Hero.tsx covers them). The canvas is aria-hidden and pointer-events-none;
 * pointer tracking is bound to the parent section.
 */
export default function HeroDotGrid() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const interactive = !reduce && fine;

    const GAP = 32; // px between dots — matches the old static texture
    const RADIUS = 150; // px influence radius around the cursor
    const accent =
      getComputedStyle(document.documentElement)
        .getPropertyValue("--accent")
        .trim() || "#c8a96e";
    const restColor =
      getComputedStyle(document.documentElement)
        .getPropertyValue("--text")
        .trim() || "#ffffff";

    let width = 0;
    let height = 0;
    const pointer = { x: -9999, y: -9999, active: false };
    let raf = 0;
    let restingPainted = false;

    function resize() {
      const rect = canvas!.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = rect.width;
      height = rect.height;
      canvas!.width = Math.round(width * dpr);
      canvas!.height = Math.round(height * dpr);
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function draw() {
      ctx!.clearRect(0, 0, width, height);
      const base = 0.05;
      for (let y = GAP / 2; y < height; y += GAP) {
        for (let x = GAP / 2; x < width; x += GAP) {
          let intensity = 0;
          if (pointer.active) {
            const dx = x - pointer.x;
            const dy = y - pointer.y;
            const dist = Math.hypot(dx, dy);
            if (dist < RADIUS) intensity = 1 - dist / RADIUS;
          }
          if (intensity > 0.01) {
            ctx!.fillStyle = accent;
            ctx!.globalAlpha = base + intensity * 0.7;
          } else {
            ctx!.fillStyle = restColor;
            ctx!.globalAlpha = base;
          }
          ctx!.beginPath();
          ctx!.arc(x, y, 1 + intensity * 1.6, 0, Math.PI * 2);
          ctx!.fill();
        }
      }
      ctx!.globalAlpha = 1;
    }

    function loop() {
      draw();
      if (!pointer.active) {
        // Paint one resting frame after the pointer leaves, then stop the loop.
        if (restingPainted) return;
        restingPainted = true;
      }
      raf = requestAnimationFrame(loop);
    }

    function startLoop() {
      cancelAnimationFrame(raf);
      restingPainted = false;
      raf = requestAnimationFrame(loop);
    }

    function onMove(e: PointerEvent) {
      if (e.pointerType !== "mouse") return;
      const rect = canvas!.getBoundingClientRect();
      pointer.x = e.clientX - rect.left;
      pointer.y = e.clientY - rect.top;
      pointer.active = true;
      startLoop();
    }

    function onLeave() {
      pointer.active = false;
      startLoop(); // paints one resting frame, then halts
    }

    resize();
    draw(); // static baseline

    const ro = new ResizeObserver(() => {
      resize();
      draw();
    });
    ro.observe(canvas!);

    const parent = canvas!.parentElement ?? canvas!;
    if (interactive) {
      parent.addEventListener("pointermove", onMove);
      parent.addEventListener("pointerleave", onLeave);
    }

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      parent.removeEventListener("pointermove", onMove);
      parent.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0 h-full w-full pointer-events-none"
    />
  );
}
